import { ThemeService } from './../../../core/services/theme.service';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, AfterViewInit, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil, startWith, debounceTime, map } from 'rxjs/operators';
import { TITLES, SPECIMEN_SOURCES } from './../../../core/data/app.data';
import { AppDataService, LancetTest } from './../../../core/services/app-data.service';
import { ContactService } from './../../../core/services/contact.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { UserService } from './../../../core/services/user.service';
import { GroupService } from 'src/app/core/services/group.service';
import { CdkDragDrop, CdkDragEnd, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatChipGrid } from '@angular/material/chips';

export interface LabTestCategory {
    name: string;
    tests: LancetTest[];
}

export interface LabTestProvider {
    id: string;
    name: string;
    enabled: boolean;
}

export interface LabTestChecklistItem {
    test: LancetTest;
    checked: boolean;
    category: string;
    specimenSource?: string | null;
}

@Component({
    selector: 'app-lab-test-selector',
    templateUrl: './lab-test-selector.component.html',
    styleUrls: ['./lab-test-selector.component.scss']
})
export class LabTestSelectorComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    protected destroy$ = new Subject<void>();
    @Input() patient: any = {};
    @Input() initialTests: LancetTest[] = [];
    @Input() testChips: LancetTest[] = [];
    @Input() checklistItems: LabTestChecklistItem[] = [];
    @Input() hintText: string = '';
    @Input() onChipDblClick: Function = () => { };
    @Input() pluralTitle: string = 'Lab Tests';
    @Input() singularTitle: string = 'Lab Test';
    @Input() collectRandValue: boolean = false;
    @Input() handleEditInternal: boolean = false;
    @Input() disableAdding: boolean = false;
    @Input() enableDragAndDrop: boolean = false;
    @Input() showCategoryFilter: boolean = true;
    @Input() showChecklist: boolean = true;
    @Input() showLabProvider: boolean = true;
    @Input() showSearch: boolean = true;
    @Input() mode: 'checklist' | 'chips' = 'checklist';
    @Output() testsChanged = new EventEmitter<LancetTest[]>();
    @Output() testDeleted = new EventEmitter<LancetTest>();
    @Output() checkedItemsChanged = new EventEmitter<LabTestChecklistItem[]>();
    @Output() checkedTestsChanged = new EventEmitter<LancetTest[]>();

    @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
    @ViewChild('chipGrid', { read: ElementRef }) chipGridElement?: ElementRef<HTMLElement>;

    @ViewChild('contactDialog') contactDialog?: TemplateRef<any>;

    testControl = new FormControl();
    filteredLabTests: Observable<LancetTest[]>;
    existingTests = new Set<string>();
    checklistItemsInternal: LabTestChecklistItem[] = [];
    specimenSourceControls = new Map<string, FormControl>();
    // Removed groupedChecklistItems - no categorization
    // Removed groupedChips - single chip list
    readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
    addOnBlur = true;

    // Lab provider management
    labProviders: LabTestProvider[] = [
        { id: 'lancet', name: 'Lancet', enabled: true },
        { id: 'pathcare', name: 'Pathcare', enabled: false },
        { id: 'ampath', name: 'Ampath', enabled: false }
    ];
    selectedProvider: string = 'lancet';

    // Removed categorization - all tests in one list

    // Single chip list - no categorization needed

    private themeSub?: Subscription;
    darkTheme!: NgxMaterialTimepickerTheme;

    titles = TITLES;
    contactMessage: any = {};
    group!: any;
    specimenSources = SPECIMEN_SOURCES;

    constructor(
        private appDataService: AppDataService,
        private dialog: MatDialog,
        private userService: UserService,
        private groupService: GroupService,
        private contactService: ContactService,
        private feedbackService: FeedbackService,
        private themeService: ThemeService,
        private renderer: Renderer2
    ) {
        this.filteredLabTests = this.testControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                startWith(''),
                debounceTime(300),
                switchMap(value => this.filterLabTests(value))
            );

        // Subscribe to user service for preferred lab
        // TODO: Implement when UserService has currentUser observable
        // this.userService.currentUser
        //   .pipe(takeUntil(this.destroy$))
        //   .subscribe((user: any) => {
        //     if (user?.preferredLab) {
        //       this.selectedProvider = user.preferredLab;
        //     }
        //   });
    }

    ngOnInit(): void {
        this.initializeTheme();
        this.loadGroupData();
        this.initializeTests();
        this.initializeChecklist();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialTests'] && changes['initialTests'].currentValue) {
            this.testChips = this.sortTestsAlphabetically([...changes['initialTests'].currentValue]);
            this.updateExistingTests();
            // Don't emit here to prevent feedback loops
        }

        if (changes['testChips'] && changes['testChips'].currentValue) {
            this.testChips = this.sortTestsAlphabetically([...changes['testChips'].currentValue]);
            this.updateExistingTests();
            // Don't emit here to prevent feedback loops
        }

        if (changes['checklistItems'] && changes['checklistItems'].currentValue) {
            this.checklistItemsInternal = this.sortChecklistItemsAlphabetically([...changes['checklistItems'].currentValue]);
            // Initialize specimenSource from test meta if not already set
            this.checklistItemsInternal.forEach(item => {
                if (item.specimenSource === undefined && (item.test as any).meta?.specimenSource) {
                    item.specimenSource = (item.test as any).meta.specimenSource;
                }
            });
        }
    }

    ngAfterViewInit(): void {
        // Any view initialization logic
        if (this.disableAdding) {
            this.testControl.disable();
        }
        else {
            this.testControl.enable();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.themeSub?.unsubscribe();
    }

    private initializeTheme(): void {
        // TODO: Implement when ThemeService has darkTheme observable
        // this.themeSub = this.themeService.darkTheme.subscribe((isDark: any) => {
        //   this.darkTheme = {
        //     container: {
        //       bodyBackgroundColor: isDark ? '#424242' : '#fff',
        //       buttonColor: isDark ? '#fff' : '#000'
        //     },
        //     dial: {
        //       dialBackgroundColor: isDark ? '#424242' : '#fff',
        //       dialEditableBackgroundColor: isDark ? '#424242' : '#fff',
        //       dialInactiveColor: isDark ? '#666' : '#ccc'
        //     },
        //     clockFace: {
        //       clockFaceBackgroundColor: isDark ? '#424242' : '#fff',
        //       clockHandColor: isDark ? '#fff' : '#000',
        //       clockFaceTimeInactiveColor: isDark ? '#666' : '#ccc'
        //     }
        //   };
        // });

        // Set default theme for now
        this.darkTheme = {
            container: {
                bodyBackgroundColor: '#fff',
                buttonColor: '#000'
            },
            dial: {
                dialBackgroundColor: '#fff',
                dialEditableBackgroundColor: '#fff',
                dialInactiveColor: '#ccc'
            },
            clockFace: {
                clockFaceBackgroundColor: '#fff',
                clockHandColor: '#000',
                clockFaceTimeInactiveColor: '#ccc'
            }
        };
    }

    private loadGroupData(): void {
        // TODO: Implement when GroupService has currentGroup observable
        // this.groupService.currentGroup
        //   .pipe(takeUntil(this.destroy$))
        //   .subscribe((group: any) => {
        //     this.group = group;
        //   });
    }

    private initializeTests(): void {
        // Initialize from testChips input if provided, otherwise from initialTests
        if (this.testChips && this.testChips.length > 0) {
            // testChips input is already set
        } else if (this.initialTests && this.initialTests.length > 0) {
            this.testChips = this.sortTestsAlphabetically([...this.initialTests]);
        } else {
            // Ensure testChips is always initialized as an empty array
            this.testChips = [];
        }
        this.updateExistingTests();
    }

    private initializeChecklist(): void {
        this.checklistItemsInternal = this.sortChecklistItemsAlphabetically([...this.checklistItems]);
        // No grouping needed
    }

    // groupChecklistItems method removed - no categorization

    private updateExistingTests(): void {
        this.existingTests.clear();
        this.testChips.forEach(test => {
            this.existingTests.add(test.orderCode);
        });
    }

    private sortTestsAlphabetically(tests: LancetTest[]): LancetTest[] {
        return tests.sort((a, b) => {
            const nameA = a.name || a.orderCode || '';
            const nameB = b.name || b.orderCode || '';
            return nameA.localeCompare(nameB);
        });
    }

    private sortChecklistItemsAlphabetically(items: LabTestChecklistItem[]): LabTestChecklistItem[] {
        return items.sort((a, b) => {
            const nameA = a.test.name || a.test.orderCode || '';
            const nameB = b.test.name || b.test.orderCode || '';
            return nameA.localeCompare(nameB);
        });
    }

    // Categorization removed - all tests in one unified list

    private filterLabTests(value: any): Observable<LancetTest[]> {
        // Convert to string and handle null, undefined, or non-string values
        const stringValue = String(value || '');
        if (!stringValue || stringValue.trim().length === 0) {
            return new Observable(observer => observer.next([]));
        }

        // For very short queries (1-2 characters), we might want to be more lenient
        const trimmedValue = stringValue.trim();

        return this.appDataService.searchCodes(trimmedValue, 'lancet').pipe(
            map(tests => {
                // Filter out already selected tests
                return tests.filter(test => !this.existingTests.has(test.orderCode));
            })
        );
    }

    onProviderChange(event: any): void {
        const providerId = event.value;
        this.selectedProvider = providerId;
        // Update user preference - this would need to be implemented in UserService
        // this.userService.updateUser({ preferredLab: providerId });
        // Clear current search and reload tests for new provider
        this.testControl.setValue('');
    }

    // Category filtering removed - all tests shown together

    sendContactMessage(): void {
        if (this.contactMessage.subject && this.contactMessage.message) {
            // This would need to be implemented in ContactService
            console.log('Contact message:', this.contactMessage);
            this.dialog.closeAll();
            // Show success message - this would need to be implemented in FeedbackService
            console.log('Message sent successfully!');
        }
    }

    closeDialog(): void {
        this.dialog.closeAll();
    }


    addToChecklist(test: LancetTest): void {
        if (this.mode === 'checklist') {
            const checklistItem: LabTestChecklistItem = {
                test: test,
                checked: true,
                category: 'general', // Default category since we removed categorization
                specimenSource: (test as any).meta?.specimenSource || null
            };

            // Check if item already exists
            const existingItem = this.checklistItemsInternal.find(item =>
                item.test.orderCode === test.orderCode
            );

            if (!existingItem) {
                this.checklistItemsInternal.push(checklistItem);
                this.checklistItemsInternal = this.sortChecklistItemsAlphabetically(this.checklistItemsInternal);
                // No grouping needed
                this.emitCheckedItemsChanged();
            }
        } else if (this.mode === 'chips') {
            this.addChip(test);
        }


        this.resetInput();
    }

    addChip(test: LancetTest): void {
        // Ensure testChips is initialized
        if (!this.testChips) {
            this.testChips = [];
        }

        // Check if item already exists
        const existingChip = this.testChips.find(chip =>
            chip.orderCode === test.orderCode
        );

        if (!existingChip) {
            this.testChips.push(test);
            this.testChips = this.sortTestsAlphabetically(this.testChips);
            this.existingTests.add(test.orderCode);
            this.emitTestsChanged();
        }

        // Clear the input field after adding chip
        this.resetInput();
    }

    removeChip(index: number, test: LancetTest): void {
        if (this.testChips && this.testChips.length > index) {
            this.testChips.splice(index, 1);
            this.existingTests.delete(test.orderCode);
            this.emitTestsChanged();
            this.testDeleted.emit(test);
        }
    }

    editChip(test: LancetTest, event: any): void {
        // Handle chip editing if needed
    }

    // groupChips method removed - no categorization needed

    // getCategoryDisplayName removed - no categories

    resetInput(): void {
        setTimeout(() => {
            console.log('resetInput');
            if (this.inputElement) {
                this.inputElement.nativeElement.value = '';
            }
            this.testControl.reset();
        }, 0);
    }

    onChipInputTokenEnd(event: any): void {
        // Handle chip input token end event
        // This ensures the input is cleared after adding a chip
        this.resetInput();
    }

    onChipInputAdd(event: any): void {
        // Handle chip input add event
        // This is triggered when a chip is added through the chip input
        console.log('Chip input add event:', event);
        this.resetInput();
    }

    // onAutocompleteSelection removed - using addChip directly

    handleDblClick(test: LancetTest, index: number): void {
        if (this.onChipDblClick) {
            this.onChipDblClick(test, index);
        }
    }

    truncateDescription(description: string, maxLength: number = 50): string {
        if (!description) return '';
        return description.length > maxLength ? description.substring(0, maxLength) + '...' : description;
    }

    getTooltipText(test: LancetTest): string {
        return `${test.orderCode} - ${test.name}`;
    }

    getDetailedTooltip(test: LancetTest): string {
        let tooltip = `${test.orderCode} - ${test.name}`;
        if (test.mnemonic) {
            tooltip += `\nMnemonic: ${test.mnemonic}`;
        }
        if (test.type) {
            tooltip += `\nType: ${test.type}`;
        }
        return tooltip;
    }

    private emitTestsChanged(): void {
        // Defer emission to next tick to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.testsChanged.emit([...this.testChips]);
        }, 0);
    }

    onChecklistItemChange(item: LabTestChecklistItem, checked: boolean): void {
        item.checked = checked;
        this.emitCheckedItemsChanged();
    }

    onSpecimenSourceChange(item: LabTestChecklistItem, value: string | null): void {
        item.specimenSource = value;
        // Update the test's meta property
        if (!(item.test as any).meta) {
            (item.test as any).meta = {};
        }
        (item.test as any).meta.specimenSource = value;
        this.emitCheckedItemsChanged();
    }

    getSpecimenSourceControl(item: LabTestChecklistItem): FormControl {
        const key = item.test.orderCode;
        if (!this.specimenSourceControls.has(key)) {
            const control = new FormControl(item.specimenSource || '');
            control.valueChanges.pipe(
                takeUntil(this.destroy$)
            ).subscribe(value => {
                this.onSpecimenSourceChange(item, value || null);
            });
            this.specimenSourceControls.set(key, control);
        } else {
            // Update control value if item's specimenSource changed externally
            const control = this.specimenSourceControls.get(key)!;
            if (control.value !== (item.specimenSource || '')) {
                control.setValue(item.specimenSource || '', { emitEvent: false });
            }
        }
        return this.specimenSourceControls.get(key)!;
    }

    getFilteredSpecimenSources(control: FormControl): Observable<string[]> {
        return control.valueChanges.pipe(
            startWith(control.value || ''),
            map(value => {
                if (!value) {
                    return this.specimenSources;
                }
                const filterValue = value.toLowerCase();
                return this.specimenSources.filter(source =>
                    source.toLowerCase().includes(filterValue)
                );
            })
        );
    }

    displaySpecimenSource(value: string | null): string {
        return value || '';
    }

    removeChecklistItem(item: LabTestChecklistItem): void {
        const index = this.checklistItemsInternal.findIndex(i =>
            i.test.orderCode === item.test.orderCode
        );
        if (index > -1) {
            this.checklistItemsInternal.splice(index, 1);
            // No grouping needed
            this.emitCheckedItemsChanged();
        }
    }

    private emitCheckedItemsChanged(): void {
        // Update test meta with specimen source before emitting
        this.checklistItemsInternal.forEach(item => {
            if (!(item.test as any).meta) {
                (item.test as any).meta = {};
            }
            if (item.specimenSource !== undefined) {
                (item.test as any).meta.specimenSource = item.specimenSource;
            }
        });

        this.checkedItemsChanged.emit([...this.checklistItemsInternal]);

        // Also emit just the checked tests
        const checkedTests = this.checklistItemsInternal
            .filter(item => item.checked)
            .map(item => item.test);
        this.checkedTestsChanged.emit(checkedTests);
    }

    getCheckedTests(): LancetTest[] {
        return this.checklistItemsInternal
            .filter(item => item.checked)
            .map(item => item.test);
    }


    openContactUsDialog(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        this.contactMessage = {
            subject: 'Lab Test Request',
            message: `I would like to request the following lab test to be added: ${this.testControl.value}`
        };

        if (this.contactDialog) {
            this.dialog.open(this.contactDialog, {
                width: '500px',
                data: this.contactMessage
            });
        }
    }

    // Drag and drop functionality
    onDrop(event: any): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(this.testChips, event.previousIndex, event.currentIndex);
            // Re-sort alphabetically after drag and drop
            this.testChips = this.sortTestsAlphabetically(this.testChips);
            this.emitTestsChanged();
        }
    }

    onDragStarted(): void {
        // Handle drag start
    }

    onDragEnded(): void {
        // Handle drag end
    }

    // Category-specific methods removed - single chip list

    getDragHandleIcon(): string {
        return 'drag_indicator';
    }
}
